import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowRightOnRectangleIcon,
  ArrowDownTrayIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  Bars3Icon,
  BellIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  DocumentMagnifyingGlassIcon,
  DocumentTextIcon,
  InboxStackIcon,
  MagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  Squares2X2Icon,
  UserGroupIcon,
  UserIcon,
  XMarkIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { IconButton, StatusBadge, ThemeToggle } from './ui';
import { classNames } from '../utils/classNames';
import { api } from '../utils/api';

type Role = 'admin' | 'operator' | 'owner';

type SearchResultSet = {
  payments: Array<{
    id: number;
    customer_name?: string;
    amount: number;
    currency: string;
    status: string;
    created_at: string;
    provider_tx_id?: string;
    processing_notes?: string;
    processed_by_username?: string;
  }>;
  withdrawals: Array<{
    id: number;
    external_trx?: string;
    amount: number;
    currency: string;
    status: string;
    created_at: string;
    account?: string;
    processing_notes?: string;
    shop_username?: string;
    processed_by_username?: string;
  }>;
  operators?: Array<{
    id: number;
    username: string;
    is_active: number;
    created_at: string;
  }>;
};

const paymentStatusTone: Record<string, React.ComponentProps<typeof StatusBadge>['tone']> = {
  pending: 'warning',
  confirmed: 'success',
  rejected: 'danger',
  cancelled: 'default',
  expired: 'default',
};

const paymentStatusText: Record<string, string> = {
  pending: 'Ожидает',
  confirmed: 'Подтвержден',
  rejected: 'Отклонен',
  cancelled: 'Отменен',
  expired: 'Истек',
};

const withdrawalStatusTone: Record<string, React.ComponentProps<typeof StatusBadge>['tone']> = {
  pending: 'warning',
  submitted: 'warning',
  processing: 'info',
  success: 'success',
  failure: 'danger',
  cancelled: 'default',
  expired: 'default',
};

const withdrawalStatusText: Record<string, string> = {
  pending: 'Ожидает',
  submitted: 'Отправлен оператору',
  processing: 'В обработке',
  success: 'Выполнен',
  failure: 'Отклонен',
  cancelled: 'Отменен',
  expired: 'Истек',
};

const formatAmount = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toLocaleString('ru-RU')} ${currency}`;
  }
};

const formatShortDate = (value: string) =>
  new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

type NavItem = {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  roles?: Role[];
  end?: boolean;
};

type NavSection = {
  title?: string;
  items: NavItem[];
};

const roleLabels: Record<Role, string> = {
  admin: 'Администратор',
  operator: 'Оператор',
  owner: 'Владелец',
};

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResultSet | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [activeSearchField, setActiveSearchField] = useState<'desktop' | 'mobile' | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const desktopSearchRef = useRef<HTMLDivElement | null>(null);
  const mobileSearchRef = useRef<HTMLDivElement | null>(null);

  const closeSearch = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    setSearchQuery('');
    setSearchResults(null);
    setSearchError(null);
    setSearchLoading(false);
    setActiveSearchField(null);
  };

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    if (!searchQuery.trim()) {
      setSearchResults(null);
      setSearchLoading(false);
      setSearchError(null);
      return;
    }

    searchDebounceRef.current = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const { data } = await api.get<SearchResultSet>('/admin/search', { params: { q: searchQuery } });
        setSearchResults(data);
        setSearchError(null);
      } catch (error) {
        setSearchError('Не удалось выполнить поиск');
        setSearchResults(null);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        desktopSearchRef.current?.contains(event.target as Node) ||
        mobileSearchRef.current?.contains(event.target as Node)
      ) {
        return;
      }
      setActiveSearchField(null);
    };

    if (activeSearchField) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeSearchField]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeSearch();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const handleSelectResult = (type: 'payment' | 'withdrawal' | 'operator', id: number) => {
    const query = searchQuery.trim();
    closeSearch();
    if (!query) return;
    if (type === 'payment') {
      navigate('/payments', { state: { initialSearch: query, paymentId: id } });
    } else if (type === 'withdrawal') {
      navigate('/withdrawals', { state: { initialSearch: query, withdrawalId: id } });
    } else if (type === 'operator') {
      navigate('/operators', { state: { initialSearch: query } });
    }
  };

  const renderSearchResults = (mode: 'desktop' | 'mobile') => {
    if (activeSearchField !== mode) return null;
    const hasResults =
      (searchResults?.payments?.length || 0) > 0 ||
      (searchResults?.withdrawals?.length || 0) > 0 ||
      (searchResults?.operators?.length || 0) > 0;

    return (
      <div className="absolute left-0 top-full z-40 mt-2 w-full max-h-[70vh] overflow-y-auto rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] shadow-2xl">
        {searchLoading ? (
          <div className="px-4 py-3 text-sm text-[var(--app-text-muted)]">Ищем…</div>
        ) : searchError ? (
          <div className="px-4 py-3 text-sm text-danger">{searchError}</div>
        ) : !hasResults ? (
          <div className="px-4 py-3 text-sm text-[var(--app-text-muted)]">Ничего не найдено</div>
        ) : (
          <div className="divide-y divide-[var(--app-border)]/65">
            {searchResults?.payments?.length ? (
              <div className="px-4 py-3">
                <p className="text-xs font-semibold uppercase text-secondary-500">Платежи</p>
                <ul className="mt-2 space-y-2">
                  {searchResults.payments.map((payment) => (
                    <li key={`payment-${payment.id}`}>
                      <button
                        onClick={() => handleSelectResult('payment', payment.id)}
                        className="w-full rounded-xl border border-transparent px-3 py-2 text-left text-sm transition hover:border-primary-200 hover:bg-[var(--app-hover)]"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-1">
                            <p className="font-semibold text-[var(--app-heading)]">#{payment.id}</p>
                            <p className="text-xs text-[var(--app-text-muted)] truncate">
                              {payment.customer_name || 'Покупатель не указан'}
                            </p>
                            {payment.provider_tx_id ? (
                              <p className="text-[11px] text-[var(--app-text-muted)] opacity-80 truncate">
                                Транзакция: {payment.provider_tx_id}
                              </p>
                            ) : null}
                            {payment.processing_notes ? (
                              <p className="text-[11px] text-[var(--app-text-muted)] opacity-80 truncate">
                                Заметки: {payment.processing_notes}
                              </p>
                            ) : null}
                            {payment.processed_by_username ? (
                              <p className="text-[11px] text-[var(--app-text-muted)] opacity-80 truncate">
                                Обработал: {payment.processed_by_username}
                              </p>
                            ) : null}
                            <p className="text-base font-semibold leading-tight text-[var(--app-heading)] tracking-tight whitespace-nowrap">
                              {formatAmount(payment.amount, payment.currency)}
                            </p>
                            <p className="text-[11px] text-[var(--app-text-muted)] opacity-80">
                              Создан: {formatShortDate(payment.created_at)}
                            </p>
                          </div>
                          <StatusBadge tone={paymentStatusTone[payment.status] || 'default'}>
                            {paymentStatusText[payment.status] || payment.status}
                          </StatusBadge>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {searchResults?.withdrawals?.length ? (
              <div className="px-4 py-3">
                <p className="text-xs font-semibold uppercase text-secondary-500">Выводы</p>
                <ul className="mt-2 space-y-2">
                  {searchResults.withdrawals.map((withdrawal) => (
                    <li key={`withdrawal-${withdrawal.id}`}>
                      <button
                        onClick={() => handleSelectResult('withdrawal', withdrawal.id)}
                        className="w-full rounded-xl border border-transparent px-3 py-2 text-left text-sm transition hover:border-primary-200 hover:bg-[var(--app-hover)]"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-1">
                            <p className="font-semibold text-[var(--app-heading)]">#{withdrawal.id}</p>
                            {withdrawal.external_trx ? (
                              <p className="text-xs text-[var(--app-text-muted)] truncate">Трекер: {withdrawal.external_trx}</p>
                            ) : null}
                            {withdrawal.account ? (
                              <p className="text-[11px] text-[var(--app-text-muted)] opacity-80 truncate">
                                Реквизиты: {withdrawal.account}
                              </p>
                            ) : null}
                            {withdrawal.processing_notes ? (
                              <p className="text-[11px] text-[var(--app-text-muted)] opacity-80 truncate">
                                Заметки: {withdrawal.processing_notes}
                              </p>
                            ) : null}
                            {withdrawal.shop_username ? (
                              <p className="text-[11px] text-[var(--app-text-muted)] opacity-80 truncate">
                                Пользователь: {withdrawal.shop_username}
                              </p>
                            ) : null}
                            {withdrawal.processed_by_username ? (
                              <p className="text-[11px] text-[var(--app-text-muted)] opacity-80 truncate">
                                Обработал: {withdrawal.processed_by_username}
                              </p>
                            ) : null}
                            <p className="text-base font-semibold leading-tight text-[var(--app-heading)] tracking-tight whitespace-nowrap">
                              {formatAmount(withdrawal.amount, withdrawal.currency)}
                            </p>
                            <p className="text-[11px] text-[var(--app-text-muted)] opacity-80">
                              Создан: {formatShortDate(withdrawal.created_at)}
                            </p>
                          </div>
                          <StatusBadge tone={withdrawalStatusTone[withdrawal.status] || 'default'}>
                            {withdrawalStatusText[withdrawal.status] || withdrawal.status}
                          </StatusBadge>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {user?.role === 'admin' && searchResults?.operators?.length ? (
              <div className="px-4 py-3">
                <p className="text-xs font-semibold uppercase text-secondary-500">Операторы</p>
                <ul className="mt-2 space-y-2">
                  {searchResults.operators.map((operator) => (
                    <li key={`operator-${operator.id}`}>
                      <button
                        onClick={() => handleSelectResult('operator', operator.id)}
                        className="w-full rounded-xl border border-transparent px-3 py-2 text-left text-sm transition hover:border-primary-200 hover:bg-[var(--app-hover)]"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-1">
                            <p className="font-semibold text-[var(--app-heading)]">{operator.username}</p>
                            <p className="text-[11px] text-[var(--app-text-muted)] opacity-80">ID: {operator.id}</p>
                            <p className="text-[11px] text-[var(--app-text-muted)] opacity-80">
                              Создан: {formatShortDate(operator.created_at)}
                            </p>
                          </div>
                          <StatusBadge tone={operator.is_active ? 'success' : 'default'}>
                            {operator.is_active ? 'Активен' : 'Неактивен'}
                          </StatusBadge>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const shopPanelTitle = useMemo(() => {
    if (user?.role === 'admin' || user?.role === 'operator') {
      return user?.shopName
        ? `Панель администратора магазина "${user.shopName}"`
        : 'Панель администратора магазина';
    }
    return 'Админ-панель';
  }, [user?.role, user?.shopName]);

  const initials = useMemo(() => {
    if (!user?.username) return 'PM';
    const parts = user.username.trim().split(/[\s._-]+/).filter(Boolean);
    const first = parts[0]?.[0];
    const second = parts[1]?.[0];
    const base = `${first ?? ''}${second ?? ''}`.toUpperCase();
    if (base) return base;
    return user.username.trim().slice(0, 2).toUpperCase();
  }, [user?.username]);

  const navigationSections = useMemo<NavSection[]>(() => {
    const role = user?.role as Role | undefined;
    const sections: NavSection[] = [
      {
        title: 'Главное',
        items: [
          {
            name: role === 'operator' ? 'Рабочий стол' : 'Обзор',
            href: role === 'operator' ? '/dashboard' : '/admin',
            icon: Squares2X2Icon,
            roles: ['admin', 'operator'],
            end: true,
          },
          { name: 'Платежи', href: '/payments', icon: BanknotesIcon, roles: ['admin', 'operator'] },
          { name: 'Выводы', href: '/withdrawals', icon: ArrowTrendingUpIcon, roles: ['admin', 'operator'] },
          { name: 'Работа с операциями', href: '/operations', icon: ArrowDownTrayIcon, roles: ['admin'] },
          { name: 'Отчеты', href: '/reports', icon: ChartBarIcon, roles: ['admin', 'operator'] },
        ],
      },
      {
        title: 'Управление',
        items: [
          { name: 'Операторы', href: '/operators', icon: UserGroupIcon, roles: ['admin'] },
          { name: 'Реквизиты', href: '/requisites', icon: InboxStackIcon, roles: ['admin'] },
          { name: 'Настройки магазина', href: '/shop-settings', icon: Cog6ToothIcon, roles: ['admin'] },
        ],
      },
      {
        title: 'Данные',
        items: [
          { name: 'Заявки на оплату', href: '/payment-requests', icon: DocumentMagnifyingGlassIcon, roles: ['operator'] },
          { name: 'Реквизиты платежей', href: '/payment-requisites', icon: ClipboardDocumentListIcon, roles: ['operator'] },
          { name: 'Покупатели', href: '/buyers', icon: UserIcon, roles: ['admin', 'operator'] },
          { name: 'Журнал аудита', href: '/audit-logs', icon: DocumentTextIcon, roles: ['admin'] },
        ],
      },
    ];
    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => !item.roles || (role && item.roles.includes(role))),
      }))
      .filter((section) => section.items.length > 0);
  }, [user?.role]);

  const mobileActions: NavSection = {
    title: 'Быстрые действия',
    items: [
      { name: 'Оповещения', href: '#alerts', icon: BellIcon },
      { name: 'Помощь', href: '#help', icon: QuestionMarkCircleIcon },
    ],
  };

  const renderSidebar = (handleNavigate?: () => void) => (
    <div className="flex h-full flex-col border-r border-[var(--app-border)] bg-[var(--app-surface)] text-[var(--app-text)]">
      <div className="flex items-center gap-3 px-6 pt-6 pb-5">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-500 text-lg font-semibold text-white shadow-soft">
          PM
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-[var(--app-heading)]">Pay Module</p>
          <p className="text-xs text-[var(--app-text-muted)]">Администрирование</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <IconButton
            aria-label="Оповещения"
            className="bg-[var(--app-surface-subtle)] shadow-none hover:bg-[var(--app-hover)]"
          >
            <BellIcon className="h-5 w-5" />
          </IconButton>
        </div>
      </div>
      <nav className="flex-1 space-y-6 overflow-y-auto px-4 pb-6">
        {navigationSections.map((section) => (
          <div key={section.title ?? 'root'}>
            {section.title ? (
              <p className="px-3 text-xs font-semibold uppercase tracking-wide text-secondary-500">{section.title}</p>
            ) : null}
            <div className="mt-2 space-y-1">
              {section.items.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href === '#alerts' ? '/payments' : item.href === '#help' ? '/help' : item.href}
                  end={item.end}
                  onClick={handleNavigate}
                  className={({ isActive }) =>
                    classNames(
                      'group flex items-center gap-3 rounded-2xl px-3 py-2 text-sm font-medium transition',
                      isActive
                        ? 'bg-primary-50 text-primary-600 shadow-soft ring-1 ring-primary-100'
                        : 'text-slate-600 hover:bg-surface-subtle hover:text-primary-600'
                    )
                  }
                >
                  <item.icon className="h-5 w-5 flex-none text-slate-400 group-hover:text-primary-600" />
                  <span className="truncate">{item.name}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
        <div className="lg:hidden">
          <p className="px-3 text-xs font-semibold uppercase tracking-wide text-secondary-500">{mobileActions.title}</p>
          <div className="mt-2 space-y-1">
            {mobileActions.items.map((item) => (
              <button
                key={item.name}
                type="button"
                onClick={() => {
                  if (item.name === 'Оповещения') {
                    alert('Раздел уведомлений скоро появится');
                  }
                  if (item.name === 'Помощь') {
                    alert('Центр помощи в разработке');
                  }
                  handleNavigate?.();
                }}
                className="group flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-left text-sm font-medium text-slate-600 transition hover:bg-surface-subtle hover:text-primary-600"
              >
                <item.icon className="h-5 w-5 flex-none text-slate-400 group-hover:text-primary-600" />
                <span className="truncate">{item.name}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--app-bg)] text-[var(--app-text)]">
      <div
        className={classNames('fixed inset-0 z-50 lg:hidden', sidebarOpen ? 'block' : 'hidden')}
        role="dialog"
        aria-modal="true"
      >
        <div className="absolute inset-0 bg-slate-900/60" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex h-full w-72 max-w-full flex-col bg-[var(--app-surface)] shadow-xl">
          <div className="flex items-center justify-between px-5 pt-5">
            <p className="text-sm font-semibold text-[var(--app-heading)]">Навигация</p>
            <IconButton aria-label="Закрыть меню" onClick={() => setSidebarOpen(false)}>
              <XMarkIcon className="h-5 w-5" />
            </IconButton>
          </div>
          <div className="mt-2 flex-1">{renderSidebar(() => setSidebarOpen(false))}</div>
        </div>
      </div>

      <div className="flex min-h-screen">
        <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
          {renderSidebar()}
        </aside>

        <div className="flex w-full flex-1 flex-col lg:pl-72">
          <header className="sticky top-0 z-40 border-b border-[var(--app-border)] bg-[var(--app-surface)]/95 backdrop-blur">
            <div className="flex items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
              <IconButton
                className="lg:hidden"
                aria-label="Открыть меню"
                onClick={() => setSidebarOpen(true)}
              >
                <Bars3Icon className="h-6 w-6" />
              </IconButton>
              <div className="flex flex-1 items-center gap-4">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-[var(--app-heading)]">{shopPanelTitle}</p>
                  {user?.shopName ? (
                    <p className="mt-0.5 text-xs text-[var(--app-text-muted)]">
                      Магазин: {user.shopName} {user.shopId ? `• ID ${user.shopId}` : null}
                    </p>
                  ) : null}
                </div>
                <div
                  ref={desktopSearchRef}
                  className="relative hidden max-w-lg flex-1 items-center gap-2 rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-1.5 shadow-sm lg:flex"
                >
                  <MagnifyingGlassIcon className="h-5 w-5 text-[var(--app-text-muted)]" />
                  <input
                    type="search"
                    placeholder="Поиск по платежам, выводам, пользователям..."
                    className="w-full border-none bg-transparent text-sm text-[var(--app-text)] outline-none placeholder:text-[var(--app-text-muted)]"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    onFocus={() => setActiveSearchField('desktop')}
                  />
                  {renderSearchResults('desktop')}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <ThemeToggle className="hidden sm:inline-flex" size="sm" />
                <ThemeToggle className="sm:hidden" size="sm" />
                <IconButton aria-label="Центр помощи" className="hidden sm:inline-flex">
                  <QuestionMarkCircleIcon className="h-5 w-5" />
                </IconButton>
                <div className="hidden items-center gap-3 rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-1.5 shadow-sm sm:flex">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-sm font-semibold text-white">
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-[var(--app-heading)]">{user?.username}</p>
                    {user?.role ? (
                      <p className="text-xs text-[var(--app-text-muted)]">{roleLabels[user.role as Role]}</p>
                    ) : null}
                  </div>
                  <button
                    onClick={() => {
                      logout();
                      navigate('/login');
                    }}
                    className="inline-flex items-center gap-1 rounded-full bg-[var(--app-surface-subtle)] px-2.5 py-1 text-xs font-semibold text-danger transition hover:text-danger/80"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                    Выйти
                  </button>
                </div>
                <IconButton
                  className="sm:hidden"
                  aria-label="Выйти из аккаунта"
                  onClick={() => {
                    logout();
                    navigate('/login');
                  }}
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </IconButton>
              </div>
            </div>
          </header>

          <div className="relative px-4 pb-3 sm:hidden">
            <div
              ref={mobileSearchRef}
              className="flex items-center gap-2 rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-2 shadow-sm"
            >
              <MagnifyingGlassIcon className="h-5 w-5 text-[var(--app-text-muted)]" />
              <input
                type="search"
                placeholder="Поиск по системе"
                className="w-full border-none bg-transparent text-sm text-[var(--app-text)] outline-none placeholder:text-[var(--app-text-muted)]"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                onFocus={() => setActiveSearchField('mobile')}
              />
              {renderSearchResults('mobile')}
            </div>
          </div>

          <main className="flex-1 px-4 py-8 sm:px-6 lg:px-10">
            <div className="mx-auto w-full max-w-7xl space-y-8">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Layout;
